<!--
Componente per la gestione delle attività di un paziente

Parametri che si ricevono dalla vista dove viene richiamato il componente

    PARAMETRO                                 TIPOLOGIA         REQUIRED      DEFAULT             DESCRIZIONE
    modal_id                                  String            FALSE         gap_modal           ID con cui viene richiamato il modale
    business_unit_id                          Number            TRUE          no default          ---
    is_super_user                             Boolean           FALSE         false               ---
    mpi_pattern_matchings                     String            FALSE         no default          Pattern matchings della business_unit di riferimento
    mpi_disable_guest_creation                Boolean           FALSE         false               Disabilita l'inserimento di nuove anagrafiche non provenienti da ricerche
    mpi_disable_search_id_mpi                 Boolean           FALSE         false               Disabilita la ricerca per ID MPI per gli utenti non superuser
    mpi_disable_guest_creation_if_search_cf   Boolean           FALSE         false               Disabilita la creazione di nuove anagrafiche se la ricerca per CF ha prodotto risultati
    mpi_additional_search_fields              Array             FALSE         no default          Campi di ricerca aggiuntivi abilitati
    open_modal_on_load                        Boolean           FALSE         false               Apre in automatico la modale di ricerca
    send_data_through_event                   Boolean           FALSE         false               Se messo a true il pulsante + emette un evento invece che rimandare alla pagina di creazione guest
    modal_title                               String            FALSE         ''                  Titolo della modale, temporaneo finchè non si sviluppa l'interfaccia ADT
    manual_edit_enabled                       Boolean           FALSE         false               Visualizza sempre il pulsante "Nuovo" se attivo
    enable_search_on_both_urls                Boolean           FALSE         false               Visualizza due bottoni secondari per permettere la ricerca su due MPI diverse
    mpi_params                                Array             FALSE         []                  Contiene i parametri di ricerca precedenti. Utilizzato nel redirect SISS ICCE
-->

<template>
  <div>
    <spinner :visible.sync="this.loading" :overlay="true"></spinner>
    <div
      aria-hidden="true"
      :aria-labelledby="modal_id"
      data-backdrop="static"
      data-keyboard="false"
      class="modal siss-anag-modal fade hide"
      v-bind:style="modal_style"
      :id="modal_id"
      role="dialog"
    >
      <div class="modal-header">
        <h3 class="title">
          <span class="fa-light fa-magnifying-glass"></span>
          <!-- {{ i18nJS.search_on_mpi }} -->
          {{ modal_title }}
          <button @click="closeModal()" aria-hidden="true" class="close" data-dismiss="modal" type="button">
            <i class="fa-light fa-xmark"></i>
          </button>
        </h3>
        <div class="text-danger" v-if="this.error_msg">
          {{ this.error_msg }}
        </div>
      </div>
      <div class="modal-body form-horizontal" v-bind:style="modal_body_style">
        <div class="row-fluid">
          <div :class="show_search_results ? 'span4' : 'span12'" id="span_search_form">
            <div class="control-group" v-if="mpi_additional_search_fields.includes('enable_search_surname')">
              <label class="control-label" for="surname">{{ i18nJS.surname }}</label>
              <div>
                <input type="text" id="surname" class="form-control input-medium" v-model="surname" @keyup.enter="searchOnMpi()" />
              </div>
            </div>
            <div class="control-group" v-if="mpi_additional_search_fields.includes('enable_search_firstname')">
              <label class="control-label" for="firstname">{{ i18nJS.firstname }}</label>
              <div>
                <input type="text" id="firstname" class="form-control input-medium" v-model="firstname" @keyup.enter="searchOnMpi()" />
              </div>
            </div>
            <div class="control-group" v-if="mpi_additional_search_fields.includes('enable_search_gender')">
              <label class="control-label" for="gender">{{ i18nJS.gender }}</label>
              <div>
                <input type="checkbox" v-model="gender_male" />
                {{ i18nJS.gender_male_extended }}
                <input type="checkbox" v-model="gender_female" />
                {{ i18nJS.gender_female_extended }}
              </div>
            </div>
            <div class="control-group" v-if="mpi_additional_search_fields.includes('enable_search_birthdate')">
              <label class="control-label">{{ i18nJS.birthdate }}</label>
              <div>
                <datepicker :value="birthdate" @input="(val) => (birthdate = val)" id="birthdate-mpi" />
              </div>
            </div>
            <div v-if="mpi_additional_search_fields.includes('enable_search_birthplace')">
              <label class="control-label">{{ i18nJS.birthplace }}</label>
              <div>
                <v-select2
                  v-model="birthplace"
                  id="birthplace"
                  :data-allow-clear="true"
                  :key="upgrade"
                  data-url="/it/countries/country_from_city.json"
                  data-ajax--url="/it/countries/autocomplete_city"
                />
              </div>
            </div>
            <div class="control-group" v-if="mpi_additional_search_fields.includes('enable_search_cf')">
              <label class="control-label" for="cf">{{ i18nJS.cf }}</label>
              <div>
                <input type="text" id="cf" class="form-control input-medium" v-model="cf" @keyup.enter="searchOnMpi()" />
                <span v-if="this.cf_error_format" class="text-danger">{{ i18nJS.errors.wrong_format }}</span>
                <span v-if="this.cf_error_max_length" class="text-danger">{{ i18nJS.errors.wrong_max_length }}</span>
                <span v-if="this.cf_error_min_length" class="text-danger"
                  >{{ i18nJS.errors.wrong_min_length }}: {{ Math.abs(16 - this.cf.length) }}</span
                >
              </div>
            </div>
            <div class="control-group" v-if="external_ids.length > 0">
              <label class="control-label" :for="external_ids[0]">{{
                external_ids.length == 1 ? i18nJS[external_ids[0]] : i18nJS.external_anagraphic_id
              }}</label>
              <v-select2
                v-if="external_ids.length > 1"
                v-model="external_id_code"
                class="input-medium js-tooltip"
                :key="upgrade"
                :title="i18nJS.clic_per_selezionare"
                :options-list="externalIdsOptionsList"
                id="select2-consent-status"
                :data-allow-clear="true"
                :data-placeholder="`${i18nJS.clic_per_selezionare} `"
                @input="external_ids[0]"
              >
              </v-select2>
              <span :class="'control'">
                <input
                  type="text"
                  id="external_id_value"
                  class="form-control input-medium"
                  :disabled="external_id_code == null || external_id_code == ''"
                  v-model="external_id_value"
                  @keyup.enter="searchOnMpi()"
                />
              </span>
            </div>
            <div class="control-group" v-if="identity_cards.length > 0">
              <label class="control-label" :for="identity_cards[0]">{{
                identity_cards.length == 1 ? i18nJS[identity_cards[0]] : i18nJS.identity_card
              }}</label>
              <v-select2
                v-if="identity_cards.length > 1"
                v-model="identity_card_code"
                class="input-medium js-tooltip"
                :key="upgrade"
                :title="i18nJS.clic_per_selezionare"
                :options-list="identityCardsOptionsList"
                id="select2-consent-status"
                :data-allow-clear="true"
                :data-placeholder="`${i18nJS.clic_per_selezionare} `"
                @input="identity_cards[0]"
              >
              </v-select2>
              <span :class="'control' + (identity_card_code != null && identity_card_code != '' ? ' obbligatorio' : '')">
                <input
                  type="text"
                  id="identity_card_value"
                  class="form-control input-medium mychzn-obbligatorio"
                  :disabled="identity_card_code == null || identity_card_code == ''"
                  v-model="identity_card_value"
                  @keyup.enter="searchOnMpi()"
                />
              </span>
            </div>
            <br />
            <div>
              <label>Campi di ricerca minimi</label>
              <div>
                <ul>
                  <li v-for="pm in patternMatchings" v-bind:key="pm.id">
                    {{ pm.description }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div v-if="show_search_results" class="span8" id="span_search_results">
            <table class="table table-hover" id="search_results">
              <thead class="sticky-on-top">
                <th scope="col" class="heading"></th>
                <th scope="col" class="heading">{{ i18nJS.fullname }}</th>
                <th scope="col" class="heading">{{ i18nJS.birthdate }}</th>
                <th scope="col" class="heading">{{ i18nJS.birthplace }}</th>
                <th scope="col" class="heading">{{ i18nJS.phone }}</th>
                <th scope="col" class="heading" v-if="center_present">{{ 'Centro' }}</th>
                <th scope="col" class="heading"></th>
              </thead>
              <tbody>
                <tr
                  v-for="item in search_results"
                  v-bind:key="item.id"
                  :class="{
                    'status-anag-present-diff': hasDiffs(item),
                    'status-anag-present': item.presence_status == 1,
                    'status-anag-non-present': item.presence_status == 2,
                  }"
                >
                  <td scope="row">
                    <div class="dropdown" style="display: table-cell">
                      <a href="#" class="dropbtn"><span class="fa-light fa-circle-info"></span></a>
                      <div class="dropdown-content">
                        <ul>
                          <li v-for="info in additionalInfo(item)" v-bind:key="info.id">
                            {{ info.message }}
                          </li>
                        </ul>
                      </div>
                    </div>
                    <div v-if="hasDiffs(item)" class="open-diffs" style="display: table-cell">
                      <a class="js-tooltip btn btn-link" id="openDiffPopup" @click="openPopupDiff(item)">
                        <i class="fa-light fa-file-lines js-tooltip" title="Log modifiche">
                          <span class="badge badge-info theme-bg">
                            <template v-for="info in additionalInfo(item)">{{ info.count }}</template>
                          </span>
                        </i>
                      </a>
                    </div>
                    <adv-setup v-if="popupDiffOpened">
                      <adv-popup-diffs
                        :opened="popupDiffOpened"
                        :diffs="infos"
                        :key="popupKey"
                        @closePopup="closePopupDiffs"
                        :setupConfig="setupConfig"
                        :title="guestGeneralities"
                      />
                    </adv-setup>
                  </td>
                  <td scope="row">
                    {{ item.surname + ' ' + item.firstname }}
                    <div class="bugiardino_link royalblue">{{ item.cf }}</div>
                  </td>
                  <td scope="row">
                    {{ item.birthdate }}
                    <div v-if="item.age" class="bugiardino_link royalblue">
                      {{ item.age + ' ' + (item.age == '1' ? i18nJS.year : i18nJS.years) }}
                    </div>
                  </td>
                  <td scope="row">
                    <div v-if="item.birthplace">
                      {{ item.birthplace.city + ' (' + item.birthplace.prov + ')' }}
                    </div>
                  </td>
                  <td scope="row">{{ item.phone }}</td>
                  <td scope="row" class="adv" v-if="center_present">
                    <div class="to-hover" style="position: relative">
                      {{ item.center_code }} <span class="adv-tooltip"> {{ item.center_description }} </span>
                    </div>
                  </td>
                  <td v-if="!send_data_through_event" scope="row">
                    <a :href="urlForImportGuest(item)" class="fa-light fa-plus pulsante_comando"></a>
                  </td>
                  <td v-if="send_data_through_event" scope="row">
                    <a @click="sendDataThroughEvent(item)" class="fa-light fa-plus pulsante_comando"></a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <template>
          <button @click="cleanSearchFilters()" class="btn btn-primary" :style="'margin-right: 20px'">
            {{ i18nJS.clean_search }}
          </button>
          <button
            :disabled="!action_disable"
            :class="'btn btn-primary' + (bothSearchesEnabled ? '' : ' hidden')"
            @click="searchOnMpi(false)"
            :style="'margin-right: 20px'"
          >
            {{ i18nJS.primary_search }}
          </button>
          <button
            :disabled="!action_disable"
            :class="'btn btn-primary' + (bothSearchesEnabled ? '' : ' hidden')"
            @click="searchOnMpi(true)"
            :style="'margin-right: 20px'"
          >
            {{ i18nJS.azero_search }}
          </button>
          <a :href="urlForNewGuest" :hidden="disableNewGuest">
            <button :disabled="!action_disable" class="btn btn-primary" :style="'margin-right: 20px'">
              {{ i18nJS.command_new }}
            </button>
          </a>
          <button :disabled="!action_disable" @click="searchOnMpi()" :class="'btn btn-primary' + (!bothSearchesEnabled ? '' : ' hidden')">
            {{ i18nJS.search }}
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import apiConnector from '@/epersonam/apiConnector';
import datepicker from '../datepicker';
import spinner from '../spinner';
import vSelect2 from '../vSelect2';
import { advPopupDiffs } from '§/components/popupDiffs';
import { LabelApi } from '@/epersonam/mixins/labels.js';
import { getCookie } from '@/epersonam/plugins/userUtils.js';

export default {
  name: 'guestSearchOnMpi',
  mixins: [LabelApi],
  props: {
    modal_id: { type: String },
    business_unit_id: { type: Number, required: true },
    is_super_user: { type: Boolean, default: false },
    mpi_pattern_matchings: { type: String },
    mpi_disable_guest_creation: { type: Boolean, default: false },
    mpi_disable_search_id_mpi: { type: Boolean, default: false },
    mpi_disable_guest_creation_if_search_cf: { type: Boolean, default: false },
    mpi_additional_search_fields: { type: Array },
    open_modal_on_load: { type: Boolean, default: false },
    send_data_through_event: { type: Boolean, default: false },
    modal_title: { type: String, default: '' },
    manual_edit_enabled: { type: Boolean, default: false },
    enable_search_on_both_urls: { type: Boolean, default: false },
    mpi_params: { type: Array, default: () => [] },
    pua: { type: Boolean, default: false },
  },
  components: {
    datepicker,
    spinner,
    vSelect2,
    advPopupDiffs,
  },
  data() {
    return {
      firstname: this.findValueOnMpiParams('firstname'),
      surname: this.findValueOnMpiParams('surname'),
      gender_male: this.findValueOnMpiParams('gender', 'M', true),
      gender_female: this.findValueOnMpiParams('gender', 'F', true),
      birthdate: this.findValueOnMpiParams('birthdate'),
      birthplace: this.findValueOnMpiParams('birthplace'),
      cf: this.findValueOnMpiParams('cf'),

      external_id_code: null,
      external_id_value: null,
      identity_card_code: null,
      identity_card_value: null,

      search_results: [],

      search_done: false,
      searched_by_cf: false,

      error_msg: null,
      loading: false,
      upgrade: true,

      cf_regexp: new RegExp(
        /^(?:[A-Z][AEIOU][AEIOUX]|[AEIOU]X{2}|[B-DF-HJ-NP-TV-Z]{2}[A-Z]){2}(?:[\dLMNP-V]{2}(?:[A-EHLMPR-T](?:[04LQ][1-9MNP-V]|[15MR][\dLMNP-V]|[26NS][0-8LMNP-U])|[DHPS][37PT][0L]|[ACELMRT][37PT][01LM]|[AC-EHLMPR-T][26NS][9V])|(?:[02468LNQSU][048LQU]|[13579MPRTV][26NS])B[26NS][9V])(?:[A-MZ][1-9MNP-V][\dLMNP-V]{2}|[A-M][0L](?:[1-9MNP-V][\dLMNP-V]|[0L][1-9MNP-V]))[A-Z]$/i,
      ),
      cf_error_format: false,
      cf_error_min_length: false,
      cf_error_max_length: false,

      base_url: window.config.ePersonamService.ePersonamHost + '/' + window.locale,

      popupDiffOpened: false,
      popupKey: 'popupkey',
      popupData: null,
      info: null,
      selectedItem: null,

      infos: null,
      setupConfig: null,
      guestGeneralities: null,
      user_idp: '',
    };
  },
  watch: {
    gender_male: function () {
      if (this.gender_male) {
        this.gender_female = false;
      }
    },
    gender_female: function () {
      if (this.gender_female) {
        this.gender_male = false;
      }
    },
    cf: function () {
      this.searched_by_cf = false;
      if (this.cf == null || this.cf.length == 0) {
        this.cf_error_format = false;
        this.cf_error_min_length = false;
        this.cf_error_max_length = false;
      } else if (this.cf.length > 16) {
        this.cf_error_max_length = true;
      } else if (this.cf?.length < 16) {
        this.cf_error_min_length = true;
        this.cf_error_format = false;
      } else {
        this.cf_error_format = !this.cf_regexp.test(this.cf);
        this.cf_error_min_length = false;
        this.cf_error_max_length = false;
      }
    },
  },
  computed: {
    action_disable() {
      return !this.cf_error_min_length && !this.cf_error_max_length && !this.cf_error_format;
    },
    i18nJS() {
      return window.i18nJS;
    },
    search_id_mpi_enabled() {
      return this.is_super_user || !this.mpi_disable_search_id_mpi;
    },
    manualEditEnabled() {
      return this.manual_edit_enabled;
    },
    bothSearchesEnabled() {
      return this.enable_search_on_both_urls;
    },
    external_ids() {
      let ids = [];

      if (this.search_id_mpi_enabled) {
        ids[ids.length] = 'id_mpi';
      }

      if (this.mpi_additional_search_fields.includes('enable_search_id_aur')) {
        ids[ids.length] = 'id_aur';
      }

      return ids;
    },
    externalIdsOptionsList() {
      let ol = [];

      for (let i = 0; i < this.external_ids.length; i++) {
        ol[ol.length] = [i18nJS[this.external_ids[i]], this.external_ids[i]];
      }

      return ol;
    },
    identity_cards() {
      let cards = [];

      // ["enable_search_birthplace","enable_search_id_aur","enable_search_tessera_sanitaria","enable_search_stp_code","enable_search_eni_code","enable_search_team_code"]
      let cards_list = ['tessera_sanitaria', 'stp_code', 'eni_code', 'team_code'];

      for (let i = 0; i < cards_list.length; i++) {
        if (this.mpi_additional_search_fields.includes('enable_search_' + cards_list[i])) {
          cards[cards.length] = cards_list[i];
        }
      }

      return cards;
    },
    identityCardsOptionsList() {
      let ol = [];

      for (let i = 0; i < this.identity_cards.length; i++) {
        ol[ol.length] = [i18nJS[this.identity_cards[i]], this.identity_cards[i]];
      }

      return ol;
    },
    gender() {
      if (this.gender_male) {
        return 'M';
      } else if (this.gender_female) {
        return 'F';
      } else {
        return null;
      }
    },
    show_search_results() {
      return this.search_results.length > 0;
    },
    modal_style() {
      return this.show_search_results
        ? {
            'min-width': '85vw !important',
            // 'margin-left': '-42.5vw !important',
          }
        : {};
    },
    modal_body_height() {
      return 400 + (this.external_ids.length > 0 ? 50 : 0) + (this.identity_cards.length > 0 ? 50 : 0);
    },
    modal_body_style() {
      return { 'min-height': this.modal_body_height + 'px' };
    },
    pageCount() {
      return this.search_results.length;
    },
    patternMatchings() {
      let pmList = [];
      let id = 0;

      let pmSplit = this.mpi_pattern_matchings.split('~');
      for (var i = 0; i < pmSplit.length; i++) {
        let tmp = [];

        if (!this.search_id_mpi_enabled && pmSplit[i] == 'id_mpi') {
          continue;
        }

        let singlePmSplit = pmSplit[i].split('^');
        for (var j = 0; j < singlePmSplit.length; j++) {
          tmp.push(i18nJS[singlePmSplit[j]]);
        }

        pmList.push({ id: id++, description: tmp.join(', ') });
      }

      return pmList;
    },
    disableNewGuest() {
      if (this.manualEditEnabled) {
        return false;
      }

      return this.mpi_disable_guest_creation || !this.search_done;
    },
    urlForNewGuest() {
      if (this.disableNewGuest) {
        return '#';
      }

      let params = [];
      params = this.addParam(params, 'guest[surname]', this.surname);
      params = this.addParam(params, 'guest[firstname]', this.firstname);
      params = this.addParam(params, 'guest[gender]', this.genderNumeric(this.gender));
      params = this.addParam(params, 'guest[birthdate]', this.birthdate);
      params = this.addParam(params, 'guest[birthplace]', this.birthplace);
      params = this.addParam(params, 'guest[cf]', this.cf);
      params = this.addParam(params, 'searched_by_cf', this.searched_by_cf);

      let uri = params.join('&');

      return '/' + window.locale + '/guests/new?' + uri;
    },
    center_present() {
      for (let i = 0; i < this.search_results.length > 0; i++) {
        if (this.search_results[i].center_code != null) {
          return true;
        }
      }

      return false;
    },
  },
  async beforeMount() {
    this.neededlabels = ['command.annull'];
    await this.setupLabels();
    this.setupConfig = {
      labels: {
        revert: this.i18nJS['command.annull'],
      },
    };
    this.popupKey += 1;
  },
  mounted() {
    if (this.external_ids.length == 1) {
      this.external_id_code = this.external_ids[0];
    }

    if (this.identity_cards.length == 1) {
      this.identity_card_code = this.identity_cards[0];
    }

    if (this.open_modal_on_load) {
      $('#mpi_search_modal').modal('show');
    }

    if (this.pua) {
      this.checkForCookieUserIdp();
    }
  },
  methods: {
    findValueOnMpiParams(keyToFind, valueToFind = null, isBool = false) {
      if (this.mpi_params.length == 0) {
        return isBool ? false : '';
      } else {
        let fieldValue;

        if (valueToFind !== null) {
          fieldValue = this.mpi_params.find(([key, value]) => key === keyToFind && value === valueToFind);
        } else {
          fieldValue = this.mpi_params.find(([key, value]) => key === keyToFind);
        }

        if (fieldValue) {
          return isBool ? true : fieldValue[1];
        } else {
          return isBool ? false : '';
        }
      }
    },
    sendDataThroughEvent(item) {
      this.closeModal();
      $('#mpi_search_modal').modal('hide');
      eventBus.$emit('guestSentFromMpi', item);
    },
    closeModal() {
      $('#mpi_search_modal').modal('hide');
      this.cleanSearchFilters();
      this.cleanSearchResults();
    },
    closePopupDiffs() {
      this.popupDiffOpened = false;
      this.infos = null;
      this.guestGeneralities = null;
      this.popupKey += 1;
    },
    openPopupDiff(item) {
      this.popupDiffOpened = true;
      this.guestGeneralities = `${item.surname} ${item.firstname} - ${item.birthdate} (${item.cf}) `;

      const popupData = this.additionalInfo(item);

      const diffsArray = popupData.filter((item) => item.diffs).map((item) => item.diffs);
      this.infos = diffsArray.flat();
      this.popupKey += 1;
    },
    redirectOrError(error) {
      if (error == 'authentication_failure')
        window.location.replace(window.config.ePersonamService.ePersonamHost + '/' + window.locale + '/guests');
      else this.error_msg = error;
      this.search_results = [];
      this.search_done = true;
    },
    cleanSearchFilters() {
      this.search_done = false;

      this.surname = null;
      this.firstname = null;
      this.gender_male = false;
      this.gender_female = false;
      this.birthdate = null;
      this.cf = null;
      this.birthplace = null;
      this.upgrade = !this.upgrade;

      if (this.external_ids.length > 1) {
        this.external_id_code = null;
      }
      this.external_id_value = null;

      if (this.identity_cards.length > 1) {
        this.identity_card_code = null;
      }
      this.identity_card_value = null;
    },
    cleanSearchResults() {
      this.search_results = [];
    },
    addGuest() {
      var guest = {
        id: this.search_results.length,
        surname: this.surname,
        firstname: this.firstname,
        gender: this.gender,
        birthdate: this.birthdate,
        cf: this.cf,
        id_mpi: this.id_mpi,
      };
      this.search_results.push(guest);
    },
    searchOnMpi(secondarySearch = null) {
      this.search_done = false;
      this.cleanSearchResults();
      this.loading = true;

      if (
        (this.external_ids.length > 1 &&
          this.external_id_code != null &&
          this.external_id_code != '' &&
          (this.external_id_value == null || this.external_id_value == '')) ||
        (this.identity_cards.length > 1 &&
          this.identity_card_code != null &&
          this.identity_card_code != '' &&
          (this.identity_card_value == null || this.identity_card_value == ''))
      ) {
        this.error_msg = i18nJS.fill_mandatory_fields_error;
        this.search_results = [];
        this.loading = false;
        return;
      }

      let params = {
        locale: window.locale,
        business_unit_id: this.business_unit_id,
        surname: this.surname,
        firstname: this.firstname,
        gender: this.gender,
        birthdate: this.birthdate,
        birthplace: this.birthplace,
        cf: this.cf ? String(this.cf).toUpperCase() : '',
      };

      if (secondarySearch != null) {
        params['secondary_search'] = secondarySearch;
      }
      params[this.external_id_code] = this.external_id_value;
      params[this.identity_card_code] = this.identity_card_value;
      if (this.pua && !!this.user_idp) {
        params['user_idp'] = this.user_idp;
      }

      apiConnector()
        .get(this.base_url + '/mpi/guests/', { params: params })
        .then(({ data }) => {
          if (data.url) {
            window.open(data.url, '_self');
          } else {
            if (this.pua && !!this.user_idp && ('warning' in data[0] || 'error' in data[0])) {
              this.error_msg = data[0]['warning']?.display || data[0]['error']?.display;
              data.shift();
            } else {
              this.error_msg = null;
            }
            this.search_results = data;
            this.loading = false;

            if (!(this.mpi_disable_guest_creation_if_search_cf && this.cf != null && this.cf != '' && data != null && data.length > 0)) {
              this.search_done = true;
            }

            if (this.mpi_disable_guest_creation_if_search_cf && this.cf != null && this.cf.length == 16) {
              this.searched_by_cf = true;
            }
          }
        })
        .catch((err) => {
          switch (err.response.status) {
            case 400:
              this.error_msg = i18nJS.search_parameters_not_valid;
              this.search_results = [];
              break;
            case 422:
              this.error_msg = i18nJS.external_service_not_configured;
              this.search_results = [];
              this.search_done = true;
              break;
            case 409:
              this.redirectOrError(err.response.data.error ?? i18nJS.external_search_error);

              break;
            case 404:
              this.error_msg = err.response?.data?.error || i18nJS.external_search_error;
              this.search_results = [];
              this.search_done = true;
              break;
            default:
              this.error_msg = i18nJS.external_search_error;
              this.search_results = [];
              this.search_done = true;
              break;
          }

          this.loading = false;
        });
    },
    urlForImportGuest(item) {
      let params = [];
      params = this.addParam(params, 'guest[surname]', item.surname);
      params = this.addParam(params, 'guest[firstname]', item.firstname);
      params = this.addParam(params, 'guest[gender]', this.genderNumeric(item.gender));
      params = this.addParam(params, 'guest[birthdate]', item.birthdate);
      params = this.addParam(params, 'guest[cf]', item.cf);
      params = this.addParam(params, 'guest[resaddress]', item.residence != null ? item.residence.street : null);
      params = this.addParam(params, 'guest[rescap]', item.residence != null ? item.residence.cap : null);
      params = this.addParam(params, 'guest[rescity_id]', item.residence != null ? item.residence.city_id : null);
      params = this.addParam(params, 'guest[domaddress]', item.domicile != null ? item.domicile.street : null);
      params = this.addParam(params, 'guest[domcap]', item.domicile != null ? item.domicile.cap : null);
      params = this.addParam(params, 'guest[domcity_id]', item.domicile != null ? item.domicile.city_id : null);
      params = this.addParam(params, 'guest[birthcity_id]', item.birthplace != null ? item.birthplace.city_id : null);
      params = this.addParam(params, 'guest[birthcountry_id]', item.birthplace != null ? item.birthplace.country_id : null);
      params = this.addParam(params, 'guest[phone]', encodeURIComponent(item.phone));
      params = this.addParam(params, 'guest[mobile]', encodeURIComponent(item.mobile));
      params = this.addParam(params, 'guest[email]', item.email);
      params = this.addParam(params, 'id_mpi', item.id_mpi);
      params = this.addParam(params, 'id_aur', item.id_aur);
      params = this.addParam(params, 'guest[codsan]', item.codsan);
      params = this.addParam(params, 'guest[rescountry_id]', item.rescountry_id);
      params = this.addParam(params, 'guest[asl_id]', item.asl_id);
      params = this.addParam(params, 'guest[distretto_id]', item.distretto_id);
      if (item.doctor_attributes) {
        params = this.addParam(params, 'guest[doctor_attributes][cod]', item.doctor_attributes.cod ?? null);
        params = this.addParam(params, 'guest[doctor_attributes][surname]', item.doctor_attributes.surname ?? null);
        params = this.addParam(params, 'guest[doctor_attributes][firstname]', item.doctor_attributes.firstname ?? null);
        params = this.addParam(params, 'guest[doctor_attributes][cf]', item.doctor_attributes.cf ?? null);
      }
      if (item.esenzioni?.length) {
        for (let i = 0; i < item.esenzioni.length; i++) {
          params = this.addParam(
            params,
            `guest[exemptions][${i}][cod]`,
            item.esenzioni[i].codiceEsenzione ? encodeURIComponent(item.esenzioni[i].codiceEsenzione) : null,
          );
          params = this.addParam(
            params,
            `guest[exemptions][${i}][description]`,
            item.esenzioni[i].descrizioneEsenzione ? encodeURIComponent(item.esenzioni[i].descrizioneEsenzione) : null,
          );
          params = this.addParam(params, `guest[exemptions][${i}][exemption_type]`, item.esenzioni[i].tipologiaEsenzione ?? null);
          params = this.addParam(params, `guest[exemptions][${i}][datein]`, item.esenzioni[i].decorrenzaEsenzione ?? null);
          params = this.addParam(params, `guest[exemptions][${i}][dateout]`, item.esenzioni[i].scadenzaEsenzione ?? null);
        }
      }
      params = this.addParam(params, 'guest[siss_last_sync]', item.ultimaVariazione ?? null);

      params = this.addParam(params, 'incomplete_guest', item.incomplete_guest);

      let uri = params.join('&');

      return '/' + window.locale + '/guests/new?' + uri;
    },
    addParam(params, key, value) {
      if (key != null && key != '' && value != null && value != '') {
        params.push(encodeURIComponent(key) + '=' + value);
      }

      return params;
    },
    genderNumeric(genderLiteral) {
      if (genderLiteral == 'M') {
        return 1;
      } else if (genderLiteral == 'F') {
        return 2;
      } else {
        return null;
      }
    },
    genderExtended(genderLiteral) {
      if (genderLiteral == 'M') {
        return i18nJS.gender_male_extended;
      } else if (genderLiteral == 'F') {
        return i18nJS.gender_female_extended;
      } else {
        return 'ND';
      }
    },
    getPresenceStatus(presence_status) {
      // presence_status
      //  1: Presente
      //  2: Non presente
      //  3: Diff

      if (presence_status == 1) {
        return 'Anagrafica presente';
      } else if (presence_status == 2) {
        return 'Anagrafica non presente';
      } else if (presence_status == 3) {
        return 'Anagrafica presente con differenze';
      }
    },
    getPresenceStatusId(presence_status) {
      // presence_status
      //  1: Presente
      //  2: Non presente
      //  3: Diff

      if (presence_status == 1) {
        return 'anag-present';
      } else if (presence_status == 2) {
        return 'anag-no-present';
      } else if (presence_status == 3) {
        return 'anag-present-diffs';
      }
    },
    hasDiffs(item) {
      if (item?.diffs?.length > 0) {
        return item?.diffs?.length;
      }
    },

    additionalInfo(item) {
      let info = [];
      let i = 0;

      if (item.id_mpi) {
        info.push({ id: i++, message: i18nJS.id_mpi + ': ' + item.id_mpi });
      }

      if (item.id_aur) {
        info.push({ id: i++, message: i18nJS.id_aur + ': ' + item.id_aur });
      }

      if (item.gender) {
        info.push({
          id: i++,
          message: i18nJS.gender + ': ' + this.genderExtended(item.gender),
        });
      }

      if (item.residence) {
        info.push({
          id: i++,
          message:
            i18nJS.residente_a +
            ': ' +
            item.residence.street +
            ', ' +
            item.residence.city +
            ' (' +
            item.residence.prov +
            '), ' +
            item.residence.cap,
        });
        if (item.residence.country)
          info.push({
            id: i++,
            message: i18nJS.citizenship + ': ' + item.residence.country + ' - ' + item.residence.country_alpha6,
          });
      }

      if (item.domicile) {
        info.push({
          id: i++,
          message:
            i18nJS.domiciliato_a +
            ': ' +
            item.domicile.street +
            ', ' +
            item.domicile.city +
            ' (' +
            item.domicile.prov +
            '), ' +
            item.domicile.cap,
        });
      }

      if (item.email) {
        info.push({ id: i++, message: i18nJS.email + ': ' + item.email });
      }

      if (item.residence_asl) {
        info.push({
          id: i++,
          message: i18nJS.residenza_asl + ': ' + item.residence_asl.asl_cod + ' - ' + item.residence_asl.district_cod,
        });
      }

      if (item.codsan) {
        info.push({ id: i++, message: i18nJS.codsan + ': ' + item.codsan });
      }

      if (item.presence_status) {
        info.push({
          id: i++,
          message: this.getPresenceStatus(item.presence_status),
          type: this.getPresenceStatusId(item.presence_status),
        });
      }

      if (item.statoPC) {
        info.push({ id: i++, message: 'StatoPC: ' + item.statoPC });
      }

      if (item?.diffs?.length) {
        const count = item.diffs.length;
        const type = 'diff';
        const infoItem = {
          id: i++,
          message: '',
          type: type,
          count: count,
          showDiffs: false,
          diffs: item.diffs,
        };
        info.push(infoItem);
      }

      return info;
    },
    checkForCookieUserIdp() {
      if (!getCookie('custom:userid_idp')) {
        return null;
      }
      this.user_idp = getCookie('custom:userid_idp');
    },
  },
};
</script>

<style lang="scss" scoped>
#cf {
  text-transform: uppercase;
}

input {
  margin-bottom: 0;
}

#mpi_search_modal.siss-anag-modal {
  //min-width: 50vw;
  left: auto;
  margin-left: auto;
  width: 50%;

  @media only screen and (max-width: 1024px) {
    width: 98%;
  }
  @media only screen and (max-width: 797px) {
    margin-left: 10px;
    width: 94%;
  }
}

#span_search_form {
  //position: fixed;
  label {
    width: 100% !important;
    display: block;
  }
  input[type='text'] {
    width: 90%;
  }
}

#span_search_results {
  position: relative;
  //margin-left: 400px;
  height: 400px;
  overflow: auto;
}

th {
  top: -2px;
  z-index: 100;
}

td {
  vertical-align: middle;
}

.dropdown-content {
  display: none;
  position: absolute;
  margin-top: 5px;
  background-color: #fff;
  min-width: 400px;
  box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.2);
  z-index: 1;
  font-size: 14px;
}

.dropdown {
  position: relative;
  display: inline-block;

  &:hover .dropdown-content {
    display: block;
  }
}

.dropbtn {
  padding: 10px;
}

.dropdown-content a:hover {
  background-color: #ddd;
}

.input-checkbox-auto {
  display: inline-block;

  padding: 0 12.5px 0 0;

  input {
    margin: 0 0 3px 0;
  }
}

.section {
  margin: 15px 0;
}

.actionsContainer {
  margin-top: 15px;
  margin-bottom: 15px;
}

.infoContainer {
  margin-top: 20px;
  width: 100%;

  .info-title {
    width: 100%;
  }

  .m_top {
    margin-top: 10px;
  }

  .wrapper {
    display: flex;

    .extended {
      width: 100%;
    }

    .choser,
    .viewer {
      width: 50%;
    }

    .choser {
      flex: 1;
    }

    .viewer {
      flex: 1;
      padding: 5px 25px;

      dl {
        margin: 0;
      }
    }
  }
}

#spinnerwrap {
  z-index: 1100;
}

.open-diffs {
  display: inline-block;
}

.status-anag-present-diff {
  td {
    background-color: #f6dcc4 !important;
  }

  .fa-file-lines {
    position: relative;

    .badge {
      font-size: 1rem;
      border-radius: 50%;
      padding: 3px 5px;
      line-height: normal;
      position: absolute;
    }
  }
}
.status-anag-present {
  td {
    background-color: #dfd !important;
  }
}
.status-anag-non-present {
  td {
    background-color: #fee !important;
  }
}

.to-hover:hover {
  cursor: pointer;

  .adv-tooltip {
    opacity: 1;
  }
}
</style>
